import React, { useCallback, useMemo, useReducer, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { makeStyles } from '@mui/styles';

import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';

import { Avatar, Button, Divider, ListItemIcon, ListItemText } from '@mui/material';
import { PersonOutlined, SettingsOutlined } from '@mui/icons-material';
import AppsIcon from '@mui/icons-material/Apps';
import UserDialog from './user-dialog';
//import ReportBugDialog from './report-bug-dialog';

import TlAlert from '../form-elements/tl-alert';
import { TlAlertDispatch } from '../form-elements/tl-alert-dispatch';

import { receiveLogout } from '../../redux/actions/login-form-actions';
import { isAdmin } from '../../services/account-service';

import colorVariables from '../../styles/variables';

const useStyles = makeStyles(() => ({
  avatarButton: {
    padding: '0 0 0 8px',
    width: 36,
    minWidth: 36,
    color: colorVariables.black,
    '&:hover': {
      backgroundColor: 'inherit',
    },
    '& .MuiButton-endIcon': {
      marginLeft: '5px',
    },
    '& .MuiAvatar-root': {
      height: '30px',
      width: '30px',
      fontSize: '12px',
      backgroundColor: colorVariables.composerMarketingPurple,
    },
  },
  menu: {
    '& .MuiPaper-root': {
      borderRadius: '10px',
      top: '48px !important',
    },
  },
  menuItemText: {
    fontSize: 14,
  },
  menuItemFirstName: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  menuItem: {
    '&.Mui-focusVisible': {
      backgroundColor: 'white',
      '&:hover': {
        backgroundColor: colorVariables.menuItemGrey,
      },
    },
  },
}));

const Account = () => {
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState(null);
  const [editUserModalIsOpen, setEditUserModalIsOpen] = useState(false);
  const [reportBugModalIsOpen, setReportBugModalIsOpen] = useState(false);

  const open = Boolean(anchorEl);
  const dispatch = useDispatch();

  const receiveFormLogout = useCallback(() => {
    dispatch(receiveLogout());
  }, [dispatch]);

  const { first_name, last_name = '' } = useSelector((/** @type {Object} */ state) => state.user);
  const account = useSelector((/** @type {Object} */ state) => state.account);
  const initials = useMemo(() => {
    return (first_name ? first_name[0] : '') + (last_name ? last_name[0] : '');
  }, [first_name, last_name]);

  const reducer = (_, action) => action;
  const [alert, alertDispatch] = useReducer(reducer, { status: null, message: '', errors: {} });

  const handleLogout = useCallback(() => {
    receiveFormLogout();
    document.location = '/';
  }, []);

  const handleClickDashboard = useCallback(() => {
    document.location = '/dashboard';
  }, []);

  const handleClickApiKeys = useCallback(() => {
    document.location = '/api-keys';
  }, []);

  const handleClickResources = useCallback(() => {
    document.location = '/resources';
  }, []);

  // const handleOpenKnowledgeBase = useCallback(() => {
  //   window.open('https://info.transmit.live/', '_blank');
  // }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenEditUserModal = useCallback(() => {
    setEditUserModalIsOpen(true);
    handleClose();
  }, []);

  const handleCloseEditUserModal = () => setEditUserModalIsOpen(false);

  // const handleOpenReportBugModal = useCallback(() => {
  //   setReportBugModalIsOpen(true);
  //   handleClose();
  // }, []);

  // const handleCloseReportBugModal = useCallback(() => {
  //   setReportBugModalIsOpen(false);
  // }, []);

  const handleOpenAdmin = useCallback(() => {
    window.location.href = '/admin';
  }, []);

  const isAdminAccount = useMemo(() => isAdmin(account), [account.name]);
  const renderAdmin = useCallback(() => {
    return (
      <MenuItem onClick={handleOpenAdmin}>
        <ListItemIcon>
          <SettingsOutlined fontSize="small" />
        </ListItemIcon>
        <ListItemText classes={{ primary: classes.menuItemText }} primary="Administration" />
      </MenuItem>
    );
  }, []);

  return (
    <TlAlertDispatch.Provider value={alertDispatch}>
      <div>
        <Button
          aria-label="account of current user"
          aria-controls="menu-appbar"
          aria-haspopup="true"
          className={classes.avatarButton}
          disableRipple
          aria-owns={anchorEl ? 'menu-appbar' : undefined}
          onClick={handleClick}
        >
          <Avatar>{initials}</Avatar>
        </Button>

        <Menu
          id="menu-appbar"
          className={classes.menu}
          anchorEl={anchorEl}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            style: { pointerEvents: 'auto' },
          }}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleClickDashboard} className={classes.menuItem}>
            <ListItemIcon>
              <AppsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText classes={{ primary: classes.menuItemText }} primary="Dashboard" />
          </MenuItem>

          <MenuItem onClick={handleOpenEditUserModal} className={classes.menuItem}>
            <ListItemIcon>
              <PersonOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText classes={{ primary: classes.menuItemText }} primary="Edit Profile" />
          </MenuItem>

          <MenuItem onClick={handleClickApiKeys} className={classes.menuItem}>
            <ListItemIcon>
              <KeyOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText classes={{ primary: classes.menuItemText }} primary="API Keys" />
          </MenuItem>

          <MenuItem onClick={handleClickResources} className={classes.menuItem}>
            <ListItemIcon>
              <AutoStoriesOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText classes={{ primary: classes.menuItemText }} primary="Resources" />
          </MenuItem>

          {/* <MenuItem onClick={handleOpenKnowledgeBase} className={classes.menuItem}>
            <ListItemIcon>
              <HelpOutlineOutlined fontSize="small" />
            </ListItemIcon>
            <Typography classes={{ body1: classes.menuItemText }}>Knowledge Base</Typography>
          </MenuItem>

          <MenuItem onClick={handleOpenReportBugModal} className={classes.menuItem}>
            <ListItemIcon>
              <OutlinedFlagTwoTone fontSize="small" />
            </ListItemIcon>
            <Typography classes={{ body1: classes.menuItemText }}>Report an issue</Typography>
          </MenuItem> */}

          <Divider />

          {isAdminAccount && renderAdmin()}

          <MenuItem onClick={handleLogout} className={classes.menuItem}>
            <ListItemIcon>
              <PowerSettingsNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText classes={{ primary: classes.menuItemText }} primary="Sign Out" />
          </MenuItem>
        </Menu>
      </div>

      <UserDialog dialogOpen={editUserModalIsOpen} handleDialogClose={handleCloseEditUserModal} />
      {/* <ReportBugDialog dialogOpen={reportBugModalIsOpen} handleDialogClose={handleCloseReportBugModal} /> */}
      <TlAlert alert={alert} />
    </TlAlertDispatch.Provider>
  );
};

export default Account;
